import { type MouseEvent } from 'react';

import type { ProductTypeOptions } from '@/types/product';

import { Checkbox } from '@/components/Checkbox';
import type { ProductFavoriteProps } from '@/components/ProductCard';
import { ProductCard } from '@/components/ProductCard';
import type { OrderAppCtaType } from '@/graphql/orderApp/api';
import { getProductTranslatedName } from '@/lib/i18n';
import { findMediaByShotType, getProductPath } from '@/lib/product';
import { getPricingFormProductChannelListing } from '@/lib/product/helpers';
import type { CtaType, Maybe } from '@/lib/types';
import * as M from '@/messages/wishlist';
import { useRegionProvider } from '@/providers/RegionProvider';
import { theme } from '@/theme';

import type { ProductWithcompare } from '../WishListWrapper/WishListWrapper';

import * as S from '../../WishListView.styles';
export interface WishProductCardProps {
  addToCart: (
    e: MouseEvent<HTMLElement>,
    id: Maybe<string>,
    sku: Maybe<string>,
    cta: CtaType | undefined,
  ) => void;
  handleChange: (product: ProductWithcompare) => void;
  handleChecked: (
    e: React.MouseEvent<HTMLElement>,
    product: ProductWithcompare,
  ) => void;
  isCheckoutProcessing: boolean;
  isClosable: boolean;
  isProcessing: boolean;
  product: ProductWithcompare;
  productCta?: OrderAppCtaType;
  productsLength: number;
  removeFavoriteItem: (
    e: { preventDefault: () => void; stopPropagation: () => void },
    product: ProductFavoriteProps,
  ) => void;
}
export const WishProductCard = ({
  addToCart,
  handleChange,
  handleChecked,
  isCheckoutProcessing,
  isClosable,
  isProcessing,
  product,
  productCta,
  productsLength,
  removeFavoriteItem,
}: WishProductCardProps) => {
  const {
    country,
    locale,
    query: { channel },
  } = useRegionProvider();
  const { compareType, defaultVariant, productType } = product;
  const cta = productCta ?? product.variantCTA;
  const productId = defaultVariant?.id;
  const sku = defaultVariant?.sku;
  const favorite = {
    type: productType.name,
    sku: sku!,
    price: defaultVariant?.pricing?.price?.gross.amount,
    theCurrency: defaultVariant?.pricing?.price?.gross.currency,
    country: country.code,
  };
  const shareableProductData = {
    sku: product.defaultVariant?.sku,
    name: product.name,
    collection: product.collections?.[0]?.name,
    image: product.media?.[0]?.url,
    url: [
      process.env.NEXT_PUBLIC_URL,
      country.code.toLowerCase(),
      '-',
      locale.slug,
      getProductPath(product, true),
    ].join(''),
  };
  const productTypeName =
    product.productType.name.toLowerCase() as ProductTypeOptions;

  // Always prefer Soldier image for wishlist product cards
  const soldierImage = findMediaByShotType(product.media ?? [], 'Soldier');
  const imageSrc = soldierImage?.url ?? product?.thumbnail?.url ?? '';
  const imageAlt = soldierImage?.alt ?? product?.thumbnail?.alt ?? '';

  return (
    <S.ProductContainer
      key={sku}
      data-test-id={`WishlistWrapperProductBox-${product.id}`}
    >
      <ProductCard
        key={sku}
        backgroundColor={theme.colors.secondaryLightGrey}
        cta={
          productId &&
          cta && (
            <ProductCard.CTA
              cta={cta}
              disabled={isCheckoutProcessing || !cta}
              isProcessing={isProcessing}
              onClick={(e) => addToCart(e, productId, sku, cta)}
            />
          )
        }
        ctaType='bottom'
        href={getProductPath(product)}
        sku={sku as string}
        type={product.productType.name}
      >
        <ProductCard.Image
          alt={`${imageAlt} ${getProductTranslatedName(product)}`}
          className={`${productTypeName}-card watch-soldier-image`}
          productType={productTypeName}
          scale={productTypeName === 'eyewear' ? 0.7 : undefined}
          src={imageSrc}
        />
        {isClosable && (
          <ProductCard.CloseIcon
            data-testid='ProductCardCloseIcon'
            onClick={(e) => {
              e.stopPropagation();
              removeFavoriteItem(e, favorite);
            }}
          />
        )}
        <ProductCard.Share shareableProductData={shareableProductData} />
        <ProductCard.Details>
          <ProductCard.Sku>{sku}</ProductCard.Sku>
          <ProductCard.Name>
            {getProductTranslatedName(product)}
          </ProductCard.Name>
          <ProductCard.Price
            price={
              product?.defaultVariant?.pricing?.price?.gross ??
              getPricingFormProductChannelListing(product, channel)?.gross
            }
          />
          {product?.productType.slug == 'watch' && productsLength > 1 && (
            <S.CompareCheckbox
              key={sku! + compareType!}
              data-testid='compareCheckbox'
              onClickCapture={(e) =>
                handleChecked(e, product satisfies ProductWithcompare)
              }
            >
              <Checkbox
                key={sku! + compareType}
                checked={compareType}
                data-test-id={`WishlistWrapperProductCard-Checkbox-${product.id}`}
                disabled={productsLength < 2}
                onChange={() =>
                  handleChange(product satisfies ProductWithcompare)
                }
                onClick={(e) => e.stopPropagation()}
              >
                {M.AddToCompare}
              </Checkbox>
            </S.CompareCheckbox>
          )}
        </ProductCard.Details>
      </ProductCard>
    </S.ProductContainer>
  );
};
